import React, { useState, useEffect, useMemo } from 'react';
import WeatherForecast from '../components/WeatherForecast';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../supabase';
import { updateClient } from '../../supabase/clients';
import { updateBooking } from '../../supabase/bookings';
import { format, isBefore } from 'date-fns';
import { ArrowLeft, Edit2, Check, X, Car, MessageSquare, Navigation, Phone, Mail, CheckCircle, Calendar, CalendarDays } from 'lucide-react';
import { getSlotDates, getSlotLabel, toET } from '../../data/constants';

const initials = (name = '') => name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

function InlineEdit({ value, onSave, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);

  useEffect(() => { if (!editing) setVal(value); }, [value, editing]);

  const save = () => { onSave(val); setEditing(false); };
  const cancel = () => { setVal(value); setEditing(false); };

  if (editing) {
    return (
      <span className="flex items-center gap-1">
        <input
          autoFocus
          value={val}
          onChange={e => setVal(e.target.value)}
          className="bg-white text-gray-900 border border-[#C9A84C] rounded px-2 py-0.5 text-sm focus:outline-none"
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
        />
        <button onClick={save}><Check size={14} className="text-emerald-600" /></button>
        <button onClick={cancel}><X size={14} className="text-gray-400" /></button>
      </span>
    );
  }

  return (
    <span
      className={`cursor-pointer hover:text-[#C9A84C] transition-colors group flex items-center gap-1 ${className}`}
      onClick={() => setEditing(true)}
    >
      {value || <span className="text-gray-300">—</span>}
      <Edit2 size={12} className="opacity-0 group-hover:opacity-100 text-gray-400" />
    </span>
  );
}

function AddressDisplay({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);

  useEffect(() => { if (!editing) setVal(value); }, [value, editing]);

  const save = () => { onSave(val); setEditing(false); };
  const cancel = () => { setVal(value); setEditing(false); };

  if (editing) {
    return (
      <span className="flex items-center gap-1">
        <input
          autoFocus
          value={val}
          onChange={e => setVal(e.target.value)}
          className="bg-white text-gray-900 border border-[#C9A84C] rounded px-2 py-0.5 text-sm focus:outline-none"
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
        />
        <button onClick={save}><Check size={14} className="text-emerald-600" /></button>
        <button onClick={cancel}><X size={14} className="text-gray-400" /></button>
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1 group">
      {value ? (
        <a
          href={`https://maps.google.com/?q=${encodeURIComponent(value)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#C9A84C] hover:underline text-sm"
        >
          {value}
        </a>
      ) : (
        <span className="text-gray-300 text-sm">—</span>
      )}
      <button
        onClick={() => setEditing(true)}
        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 ml-1"
      >
        <Edit2 size={12} />
      </button>
    </span>
  );
}

function QuickActions({ client, bookings }) {
  const [sending, setSending] = useState(false);
  const [etaInput, setEtaInput] = useState('');
  const [showEtaField, setShowEtaField] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState('');

  const nextBooking = bookings.find(b => {
    if (!b.scheduled_at) return false;
    return !isBefore(new Date(b.scheduled_at), new Date()) && b.status === 'scheduled';
  });

  const sendOnMyWay = async () => {
    setSending(true);
    setFeedbackMsg('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch('/api/send-admin-sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          type: 'enroute',
          clientPhone: client.phone,
          clientName: client.name,
          clientAddress: client.address,
          eta: etaInput || null,
        }),
      });
      if (!response.ok) throw new Error((await response.json()).error);
      setFeedbackMsg(`Text sent to ${client.name}!`);
      setShowEtaField(false);
      setEtaInput('');
    } catch (err) {
      setFeedbackMsg(`Failed: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm">
      <h4 className="font-semibold text-gray-800 text-sm mb-3">Quick Actions</h4>
      <div className="space-y-2">
        {showEtaField ? (
          <div className="space-y-2">
            <input
              type="text"
              value={etaInput}
              onChange={e => setEtaInput(e.target.value)}
              placeholder="ETA (e.g. 15 minutes) — optional"
              className="w-full bg-white text-gray-900 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
            />
            <div className="flex gap-2">
              <button
                onClick={sendOnMyWay}
                disabled={sending}
                className="flex-1 bg-[#C9A84C] text-black rounded-lg py-2 text-xs font-semibold hover:bg-[#b8943d] disabled:opacity-50"
              >
                {sending ? 'Sending…' : 'Send Text'}
              </button>
              <button
                onClick={() => { setShowEtaField(false); setEtaInput(''); }}
                className="border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowEtaField(true)}
            className="w-full flex items-center gap-2 bg-[#C9A84C] text-black rounded-lg px-3 py-2.5 text-sm font-semibold hover:bg-[#b8943d] transition-colors"
          >
            <Navigation size={15} />
            On My Way
          </button>
        )}

        {feedbackMsg && (
          <p className={`text-xs px-2 py-1 rounded ${feedbackMsg.startsWith('Failed') ? 'text-red-600 bg-red-50' : 'text-emerald-600 bg-emerald-50'}`}>
            {feedbackMsg}
          </p>
        )}

        {client.phone && (
          <div className="flex gap-2">
            <a
              href={`tel:${client.phone}`}
              className="flex-1 flex items-center justify-center gap-1.5 border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Phone size={14} className="text-gray-400" />
              Call
            </a>
            <a
              href={`sms:${client.phone}`}
              className="flex-1 flex items-center justify-center gap-1.5 border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <MessageSquare size={14} className="text-gray-400" />
              Text
            </a>
          </div>
        )}

        {client.email && (
          <a
            href={`mailto:${client.email}`}
            className="w-full flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Mail size={15} className="text-gray-400" />
            Email Client
          </a>
        )}

        {nextBooking && (
          <div className="bg-blue-50 rounded-lg px-3 py-2.5 text-xs text-blue-700 flex items-center gap-2">
            <Calendar size={13} />
            <span>Next: {format(toET(new Date(nextBooking.scheduled_at)), 'EEE, MMM d · h:mm a')}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ClientProfile() {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('clients').select('*').eq('id', clientId).single()
      .then(({ data }) => { if (data) setClient(data); setLoading(false); });

    const channel = supabase
      .channel(`client-${clientId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'clients', filter: `id=eq.${clientId}` },
        (payload) => setClient(payload.new))
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [clientId]);

  useEffect(() => {
    supabase.from('bookings').select('*').eq('client_id', clientId).order('scheduled_at', { ascending: false })
      .then(({ data }) => setBookings(data || []));

    const channel = supabase
      .channel(`bookings-${clientId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bookings', filter: `client_id=eq.${clientId}` },
        () => {
          supabase.from('bookings').select('*').eq('client_id', clientId).order('scheduled_at', { ascending: false })
            .then(({ data }) => setBookings(data || []));
        })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [clientId]);

  const save = (field) => async (val) => {
    setClient(prev => ({ ...prev, [field]: val }));
    const { error } = await updateClient(clientId, { [field]: val });
    if (error) {
      setClient(prev => ({ ...prev, [field]: prev[field] }));
      console.error('Failed to save:', error.message);
    }
  };

  const predictedDates = useMemo(() => {
    if (!client || client.type !== 'maintenance' || !client.slot) return [];
    return getSlotDates(client.slot, 6);
  }, [client]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C9A84C]" />
      </div>
    );
  }

  if (!client) {
    return <div className="p-6"><p className="text-gray-500">Client not found.</p></div>;
  }

  const totalSpend = bookings.reduce((s, b) => s + (b.price || 0), 0);
  const completedBookings = bookings.filter(b => b.status === 'completed');
  const upcomingBookings = bookings.filter(b => {
    if (!b.scheduled_at) return false;
    return !isBefore(new Date(b.scheduled_at), new Date()) && b.status !== 'cancelled';
  });

  return (
    <div className="p-6 pb-24 md:pb-6 space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/admin/clients" className="text-gray-400 hover:text-gray-700">
          <ArrowLeft size={20} />
        </Link>
        <h2 className="text-xl font-bold text-gray-900">{client.name}</h2>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          client.type === 'maintenance'
            ? 'bg-yellow-100 text-yellow-800 border border-[#C9A84C]'
            : 'bg-blue-100 text-blue-800'
        }`}>
          {client.type === 'maintenance' ? 'Maintenance' : 'Weekend'}
        </span>
        {(() => {
          const county = client.county || '';
          const allowed = ["St. Mary's County", "Charles County", "Calvert County"];
          if (county && !allowed.includes(county)) {
            return <span className="text-xs bg-orange-100 text-orange-700 border border-orange-300 px-2 py-0.5 rounded-full font-semibold">Out of Area</span>;
          }
          return null;
        })()}
        <Link
          to={`/admin/bookings/new?clientId=${clientId}`}
          className="ml-auto flex items-center gap-1.5 bg-[#C9A84C] hover:bg-[#b8943d] text-black text-sm font-semibold px-4 py-1.5 rounded-lg"
        >
          + New Booking
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-4">
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-4 mb-5">
              <div className="w-14 h-14 rounded-full bg-[#C9A84C] flex items-center justify-center text-black text-xl font-bold">
                {initials(client.name)}
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">
                  <InlineEdit value={client.name} onSave={save('name')} />
                </h3>
              </div>
            </div>

            <div className="space-y-3 text-sm">
              {[
                { label: 'Email', field: 'email' },
                { label: 'Phone', field: 'phone' },
              ].map(({ label, field }) => (
                <div key={field} className="flex items-start gap-2">
                  <span className="text-gray-400 w-16 shrink-0 text-xs pt-0.5">{label}</span>
                  <InlineEdit value={client[field] || ''} onSave={save(field)} className="text-gray-800" />
                </div>
              ))}
              <div className="flex items-start gap-2">
                <span className="text-gray-400 w-16 shrink-0 text-xs pt-0.5">Address</span>
                <AddressDisplay value={client.address || ''} onSave={save('address')} />
              </div>
              {client.type === 'maintenance' && client.slot && (
                <div className="flex items-start gap-2">
                  <span className="text-gray-400 w-16 shrink-0 text-xs pt-0.5">Slot</span>
                  <span className="text-gray-800 text-sm">{getSlotLabel(client.slot)}</span>
                </div>
              )}
              <div className="flex items-start gap-2">
                <span className="text-gray-400 w-16 shrink-0 text-xs pt-0.5">Status</span>
                <select
                  value={client.status || 'active'}
                  onChange={async (e) => {
                    await updateClient(clientId, { status: e.target.value });
                    await supabase.from('activity_log').insert({
                      note: `Client marked ${e.target.value}: ${client.name}`,
                      type: 'client_update',
                      client_id: clientId,
                    });
                  }}
                  className="bg-white text-gray-900 text-sm border border-gray-200 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-[#C9A84C]"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h4 className="font-semibold text-gray-800 text-sm mb-3">Stats</h4>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Total Visits', value: completedBookings.length },
                { label: 'Total Spend', value: `$${totalSpend.toLocaleString()}` },
                { label: 'Avg Ticket', value: completedBookings.length ? `$${Math.round(totalSpend / completedBookings.length)}` : '—' },
                { label: 'Lifetime Value', value: `$${(client.lifetime_value || 0).toLocaleString()}` },
              ].map(s => (
                <div key={s.label} className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-400">{s.label}</p>
                  <p className="font-bold text-gray-900 text-sm mt-0.5">{s.value}</p>
                </div>
              ))}
            </div>
          </div>

          <QuickActions client={client} bookings={bookings} />

          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Car size={14} className="text-gray-500" />
              <h4 className="font-semibold text-gray-800 text-sm">Vehicle</h4>
            </div>
            <p className="text-gray-700 text-sm">
              {client.year && client.make
                ? `${client.year} ${client.make} ${client.model || ''} ${client.color ? `· ${client.color}` : ''}`
                : 'No vehicle on record.'}
            </p>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          {client.type === 'maintenance' && predictedDates.length > 0 && (
            <div className="bg-purple-50 border border-purple-100 rounded-2xl p-5">
              <h4 className="font-semibold text-purple-800 text-sm mb-1 flex items-center gap-2">
                <CalendarDays size={14} /> Upcoming Schedule
              </h4>
              <p className="text-xs text-purple-400 mb-3">
                Based on slot: {getSlotLabel(client.slot)} · Next 6 months
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {predictedDates.map((date, i) => (
                  <div key={i} className="flex items-center gap-2 p-2.5 bg-white rounded-xl border border-purple-100">
                    <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-sm text-gray-700 font-medium">
                      {format(date, 'EEE, MMM d, yyyy')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {upcomingBookings.length > 0 && (
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
              <h4 className="font-semibold text-blue-800 text-sm mb-3 flex items-center gap-2">
                <Calendar size={14} /> Upcoming ({upcomingBookings.length})
              </h4>
              <div className="space-y-2">
                {upcomingBookings.map(b => (
                  <div key={b.id} className="p-3 bg-white rounded-xl border border-blue-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-800 text-sm">{b.tier || b.service_type || 'Service'}</p>
                        <p className="text-xs text-gray-400">{format(toET(new Date(b.scheduled_at)), 'EEE, MMM d, yyyy · h:mm a')}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">${b.price || 0}</span>
                        <Link to={`/admin/jobs/${b.id}`} className="text-xs text-[#C9A84C] hover:underline">View</Link>
                      </div>
                    </div>
                    <WeatherForecast
                      scheduledAt={b.scheduled_at}
                      address={b.address || client.address}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-800">Service History ({bookings.length})</h4>
            </div>
            {bookings.length === 0 ? (
              <p className="text-gray-400 text-sm">No bookings yet.</p>
            ) : (
              <div className="space-y-2">
                {bookings.map(b => {
                  const d = toET(new Date(b.scheduled_at));
                  const isPast = isBefore(new Date(b.scheduled_at), new Date());
                  return (
                    <div key={b.id} className={`flex items-center justify-between p-3 rounded-xl ${isPast ? 'bg-gray-50' : 'bg-blue-50 border border-blue-100'}`}>
                      <div className="flex items-start gap-3">
                        {b.status === 'completed' && (
                          <CheckCircle size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                        )}
                        <div>
                          <p className="font-medium text-gray-800 text-sm">{b.tier || b.service_type || 'Service'}</p>
                          <p className="text-xs text-gray-400">{format(d, 'MMM d, yyyy')}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <StatusChip status={b.status} bookingId={b.id} />
                        <span className="font-semibold text-sm text-gray-900">${b.price || 0}</span>
                        <Link to={`/admin/jobs/${b.id}`} className="text-xs text-[#C9A84C] hover:underline">View</Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <MessageSquare size={14} /> Notes
            </h4>
            <textarea
              defaultValue={client.notes || ''}
              onBlur={e => updateClient(clientId, { notes: e.target.value })}
              rows={4}
              placeholder="Add notes about this client…"
              className="w-full bg-white text-gray-900 text-sm border border-gray-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#C9A84C] resize-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusChip({ status, bookingId }) {
  const [updating, setUpdating] = useState(false);
  const STATUS_COLORS = {
    scheduled: 'bg-blue-100 text-blue-800',
    'in-progress': 'bg-yellow-100 text-yellow-800',
    completed: 'bg-emerald-100 text-emerald-800',
    cancelled: 'bg-red-100 text-red-700',
  };
  const handleChange = async (e) => {
    setUpdating(true);
    try { await updateBooking(bookingId, { status: e.target.value }); }
    finally { setUpdating(false); }
  };
  return (
    <select
      value={status || 'scheduled'}
      disabled={updating}
      onChange={handleChange}
      className={`text-xs border-0 rounded-full px-2 py-1 font-medium focus:outline-none focus:ring-1 focus:ring-[#C9A84C] ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'}`}
    >
      <option value="scheduled">Scheduled</option>
      <option value="in-progress">In Progress</option>
      <option value="completed">Completed</option>
      <option value="cancelled">Cancelled</option>
    </select>
  );
}
